import { Trans, useTranslation } from 'react-i18next';
import { AntdForm, AntdInput, AntdTypography, Button } from '@ui/components';
import { CONTACT_SUPPORT_KEY as KEY } from '../../constants';
import { ContactSupportFormValues } from '../../types';
import { useContactSupportForm } from './hooks';

const { Text } = AntdTypography;

type ContactSupportFormProps = {
  initialValues: Partial<ContactSupportFormValues>;
  isSubmitting: boolean;
  onCancel: () => void;
  onRecaptchaError: (error: unknown, values: ContactSupportFormValues) => void;
  onSubmit: (values: ContactSupportFormValues, recaptchaToken: string) => void;
};

export const ContactSupportForm = ({
  initialValues,
  isSubmitting,
  onCancel,
  onRecaptchaError,
  onSubmit,
}: ContactSupportFormProps) => {
  const { t } = useTranslation();
  const { isRecaptchaReady, isVerifying, handleFinish } = useContactSupportForm({
    onSubmit,
    onRecaptchaError,
  });
  const isBusy = isSubmitting || isVerifying;

  return (
    <AntdForm<ContactSupportFormValues>
      layout="vertical"
      requiredMark={false}
      initialValues={initialValues}
      onFinish={handleFinish}
      className="flex flex-col"
    >
      <Text className="text-base font-semibold text-base-800 mb-3">{t(`${KEY}.form_title`)}</Text>
      <AntdForm.Item
        name="searchTerm"
        label={t(`${KEY}.search_term_label`)}
        rules={[{ required: true, whitespace: true, message: t(`${KEY}.search_term_required`) }]}
      >
        <AntdInput maxLength={200} />
      </AntdForm.Item>
      <AntdForm.Item name="synonyms" label={t(`${KEY}.synonyms_label`)}>
        <AntdInput.TextArea
          rows={2}
          maxLength={1000}
          placeholder={t(`${KEY}.synonyms_placeholder`)}
        />
      </AntdForm.Item>
      <AntdForm.Item
        name="email"
        label={t(`${KEY}.email_label`)}
        extra={t(`${KEY}.email_help`)}
        rules={[{ required: true, type: 'email', message: t(`${KEY}.email_invalid`) }]}
      >
        <AntdInput type="email" autoComplete="email" />
      </AntdForm.Item>
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel} disabled={isBusy}>
          {t(`${KEY}.cancel`)}
        </Button>
        <Button type="primary" htmlType="submit" loading={isBusy || !isRecaptchaReady}>
          {t(`${KEY}.send`)}
        </Button>
      </div>
      <Text className="text-xs text-base-600 mt-3">
        <Trans
          t={t}
          i18nKey={`${KEY}.recaptcha_notice`}
          components={{
            privacy: (
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" />
            ),
            terms: <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" />,
          }}
        />
      </Text>
    </AntdForm>
  );
};
